import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Icon } from "semantic-ui-react";
import { EvidenceText } from "../../../api";
import { useAuth } from "../../../hooks";
import { useLanguage } from "../../../contexts";
import { useSite } from "../../../contexts/SiteContext";
import { useCompany } from "../../../contexts/CompanyContext";
import { isMaster } from "../../../utils/checkPermission";
import {
  EvidenceTextViewer,
  EVIDENCE_MODULES,
  EVIDENCE_VALIDITIES,
  moduleLabelKey,
  validityLabelKey,
  formatDate,
  highlight,
  evidenceDocumentUrl,
} from "../../../components/Shared/EvidenceTextViewer";
import "./EvidenceSearch.scss";

const evidenceTextController = new EvidenceText();
const PAGE_SIZE_OPTIONS = [10, 20];

// Dropdown con el mismo look de pill celeste (texto en negrita, menú blanco
// redondeado) que los filtros de Plan de Acción — reemplaza el <select>
// nativo, cuyas opciones no se pueden estilizar entre navegadores. Al elegir
// una opción aparece la "✕" para limpiarla sin reabrir el menú.
const FilterSelect = ({ options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const hasValue = Boolean(selected?.value);

  return (
    <div className="evs-fd" ref={ref}>
      <button type="button" className="evs-fd__toggle" onClick={() => setOpen((v) => !v)}>
        <span className={hasValue ? "evs-fd__value" : "evs-fd__placeholder"}>{selected ? selected.text : placeholder}</span>
        <span className="evs-fd__icons">
          {hasValue && (
            <span
              className="evs-fd__clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
              title={placeholder}
            >
              ✕
            </span>
          )}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`evs-fd__arrow${open ? " evs-fd__arrow--open" : ""}`}>
            <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="evs-fd__menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`evs-fd__item${o.value === value ? " evs-fd__item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export function EvidenceSearch() {
  const { accessToken, user } = useAuth();
  // Buscar y analizar evidencias es solo para master (el servidor también lo exige)
  const canSearch = isMaster(user?.role);
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [searchParams, setSearchParams] = useSearchParams();
  // Misma empresa/sitio elegidos arriba (como el resto de Gestión): si hay
  // un sitio, las evidencias se acotan a ese sitio; si solo hay empresa
  // (master), a esa empresa. El servidor igual limita al resto a la suya.
  const { site: contextSite } = useSite();
  const { company: contextCompany } = useCompany();
  const noSiteInCtx = !contextSite || contextSite === "0" || contextSite === 0;
  const noCompanyInCtx = !contextCompany || contextCompany === "0" || contextCompany === 0;
  const scopeCompany = noCompanyInCtx ? undefined : contextCompany;

  const [input, setInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [module, setModule] = useState("");
  // Filtro de vigencia: viene en la URL cuando se llega desde la campanita (?validity=outdated)
  const [validity, setValidity] = useState(() => {
    const v = searchParams.get("validity");
    return EVIDENCE_VALIDITIES.includes(v) ? v : "";
  });
  const [searched, setSearched] = useState("");  // última búsqueda ejecutada
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null);   // { module, fileKey, fileName }
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  // Links de la campanita: ?open=modulo:fileKey abre el visor de esa evidencia
  useEffect(() => {
    const v = searchParams.get("validity");
    if (EVIDENCE_VALIDITIES.includes(v)) setValidity(v);
    const open = searchParams.get("open");
    if (open && open.includes(":")) {
      const sep = open.indexOf(":");
      setViewing({ module: open.slice(0, sep), fileKey: open.slice(sep + 1), fileName: "" });
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadRecent = useCallback(async () => {
    if (!canSearch) return;
    setLoading(true);
    try {
      setRecent(await evidenceTextController.getEvidenceTexts(accessToken, {
        module: module || undefined,
        validity: validity || undefined,
        company: scopeCompany,
        site: noSiteInCtx ? undefined : contextSite,
      }));
    } catch {
      setRecent([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, module, validity, contextSite, noSiteInCtx, scopeCompany, canSearch]);

  const runSearch = useCallback(async (q) => {
    if (!canSearch) return;
    setError("");
    setLoading(true);
    try {
      setResults(await evidenceTextController.search(accessToken, q, module || undefined, validity || undefined, noSiteInCtx ? undefined : contextSite, scopeCompany));
      setSearched(q);
    } catch (err) {
      setResults([]);
      setSearched(q);
      setError(err?.msg || t("ev_error_loading"));
    } finally {
      setLoading(false);
    }
  }, [accessToken, module, validity, contextSite, noSiteInCtx, scopeCompany, canSearch]);

  // Búsqueda en vivo: espera una pausa al escribir antes de disparar la query.
  useEffect(() => {
    const q = input.trim();
    const timer = setTimeout(() => setDebouncedQuery(q), 350);
    return () => clearTimeout(timer);
  }, [input]);

  // Sin búsqueda activa (o con menos de 2 caracteres) se muestran las últimas
  // evidencias leídas; al cambiar un filtro con una búsqueda activa, se repite
  // la búsqueda con el filtro nuevo. Después, filtra sobre esos resultados.
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      runSearch(debouncedQuery);
    } else {
      setSearched("");
      setError("");
      loadRecent();
    }
  }, [debouncedQuery, module, validity, contextSite, scopeCompany]);

  // Cambiar búsqueda, filtros o tamaño de página siempre vuelve a la página 1.
  useEffect(() => { setPage(1); }, [debouncedQuery, module, validity, pageSize, contextSite, scopeCompany]);

  const onValidityChange = (value) => {
    setValidity(value);
    const next = new URLSearchParams(searchParams);
    if (value) next.set("validity", value); else next.delete("validity");
    setSearchParams(next, { replace: true });
  };

  const clear = () => setInput("");

  const closeViewer = useCallback(() => setViewing(null), []);

  const renderValidity = (item) => {
    if (!item.validity || item.validity === "valid") return null;
    const date = item.expiryDate ? formatDate(item.expiryDate) : formatDate(item.docDate);
    return (
      <span className={`ev-chip ev-chip--validity-${item.validity}`} title={date}>
        {t(validityLabelKey(item.validity))}{date ? ` · ${date}` : ""}
      </span>
    );
  };

  const renderCard = (item, body) => {
    // Si el archivo se subió sin nombre real (p.ej. fotos de checklist desde
    // el celular), el backend guardó una referencia legible al registro
    // (nombre del checklist, título del plan, etc.) para mostrar en su lugar.
    const displayName = item.refLabel || item.fileName;
    return (
    <li key={`${item.module}-${item.fileKey}`} className="evs-card">
      <div className="evs-card__icon"><Icon name="file alternate outline" /></div>
      <div className="evs-card__main">
        <div className="evs-card__top">
          <span className="evs-card__module">{t(moduleLabelKey(item.module))}</span>
          <span className="evs-card__name" title={item.fileName}>{displayName}</span>
          {item.status && item.status !== "done" && (
            <span className={`ev-chip ev-chip--${item.status}`}>{t(`ev_status_${item.status}`)}</span>
          )}
          {renderValidity(item)}
        </div>
        {body}
        <div className="evs-card__meta">
          {item.refLabel && <span className="evs-card__file"><Icon name="file outline" />{item.fileName}</span>}
          {item.refDate && <span><Icon name="calendar outline" />{formatDate(item.refDate)}</span>}
          {item.expiryDate && <span><Icon name="hourglass half" />{t("ev_expires_on")} {formatDate(item.expiryDate)}</span>}
          {item.latestDate && <span><Icon name="calendar outline" />{t("ev_latest_date")}: {formatDate(item.latestDate)}</span>}
          {item.createdAt && <span><Icon name="upload" />{t("ev_uploaded")}: {formatDate(item.createdAt)}</span>}
        </div>
      </div>
      <div className="evs-card__actions">
        <button
          type="button"
          className="ev-btn ev-btn--ghost evs-card__open evs-card__open--primary"
          onClick={() => setViewing({ module: item.module, fileKey: item.fileKey, fileName: item.fileName })}
        >
          <Icon name="eye" /> {t("ev_view_text")}
        </button>
        {evidenceDocumentUrl(item.module, item.refId) && (
          <a
            href={evidenceDocumentUrl(item.module, item.refId)}
            target="_blank"
            rel="noopener noreferrer"
            className="ev-btn ev-btn--ghost evs-card__open"
          >
            <Icon name="external alternate" /> {t("ev_view_document")}
          </a>
        )}
      </div>
    </li>
    );
  };

  const showingSearch = Boolean(searched);
  const fullList = showingSearch ? results : recent;
  const totalPages = Math.max(1, Math.ceil(fullList.length / pageSize));
  const pageClamped = Math.min(page, totalPages);
  const list = fullList.slice((pageClamped - 1) * pageSize, pageClamped * pageSize);

  const moduleOptions = [
    { key: "", value: "", text: t("ev_all_modules") },
    ...EVIDENCE_MODULES.map((m) => ({ key: m.value, value: m.value, text: t(m.labelKey) })),
  ];
  const validityOptions = [
    { key: "", value: "", text: t("ev_all_validities") },
    ...EVIDENCE_VALIDITIES.map((v) => ({ key: v, value: v, text: t(validityLabelKey(v)) })),
  ];

  if (!canSearch) return <Navigate to="/admin" replace />;

  return (
    <div className="evidence-search-page">
      <div className="evs-header">
        <span className="evs-header__title">{t("evidence_search")}</span>
      </div>

      <p className="evs-intro">{t("ev_search_intro")}</p>

      <div className="evs-bar">
        <div className="evs-bar__input">
          <Icon name={loading ? "spinner" : "search"} loading={loading} />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("search")}
            aria-label={t("evidence_search")}
          />
          {input && (
            <button type="button" className="evs-bar__clear" onClick={clear} aria-label={t("ev_clear")}>
              <Icon name="close" />
            </button>
          )}
        </div>
        <FilterSelect options={moduleOptions} value={module} onChange={setModule} placeholder={t("ev_all_modules")} />
        <FilterSelect options={validityOptions} value={validity} onChange={onValidityChange} placeholder={t("ev_all_validities")} />
      </div>

      {error && <p className="evs-error">{error}</p>}

      <div className="evs-section-title">
        {showingSearch
          ? <>{results.length} {t("ev_results")} · “{searched}”</>
          : validity ? <>{t(validityLabelKey(validity))} · {recent.length} {t("ev_results")}</> : t("ev_recent")}
      </div>

      {loading ? (
        <p className="evs-empty"><Icon loading name="spinner" /></p>
      ) : list.length === 0 ? (
        <p className="evs-empty">{showingSearch || validity ? t("ev_no_results") : t("ev_no_evidences")}</p>
      ) : (
        <>
          <ul className="evs-list">
            {showingSearch
              ? list.map((r) => renderCard(r, <p className="evs-card__snippet">{highlight(r.snippet, searched)}</p>))
              : list.map((r) => renderCard(r, null))}
          </ul>

          <div className="evs-pagination">
            <div className="evs-pagination__size">
              <span>{t("ev_show")}</span>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`evs-pagination__size-btn${pageSize === n ? " evs-pagination__size-btn--active" : ""}`}
                  onClick={() => setPageSize(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="evs-pagination__nav">
                <button
                  type="button"
                  className="evs-pagination__nav-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pageClamped === 1}
                  aria-label="previous"
                >
                  <Icon name="angle left" />
                </button>
                <span className="evs-pagination__nav-label">{t("ev_page")} {pageClamped} {t("ev_of")} {totalPages}</span>
                <button
                  type="button"
                  className="evs-pagination__nav-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={pageClamped === totalPages}
                  aria-label="next"
                >
                  <Icon name="angle right" />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <EvidenceTextViewer
        open={Boolean(viewing)}
        onClose={closeViewer}
        module={viewing?.module}
        fileKey={viewing?.fileKey}
        fileName={viewing?.fileName}
        initialQuery={searched}
      />
    </div>
  );
}
